/**
 * Phase 530 wave 4 task 4.3 (#1939) — the ONE place a `StatusMilestoneKinds` value becomes a
 * sentence.
 *
 * <h3>The engine ships KINDS; the portal ships ENGLISH</h3>
 * <p>`platform/core/JobStore/StatusView/StatusMilestoneKinds.cs` is a CLOSED set of nine
 * identities — `stage_entered`, `party_outcome`, `completed` — and its own header states the deal
 * this file is the other half of: "A kind is an IDENTITY, not a sentence … The words a staffer
 * reads are composed by whatever renders them", which is what keeps plain English, and therefore
 * one client's vocabulary, out of the shared engine (fence `engine-client-logic`, ADR-023 Part 2,
 * D4). The engine also deliberately drops the raw `job_events.event_type` on the way out, so this
 * module never sees one: there is exactly one vocabulary to write copy for, and no second, lower
 * altitude for a surface to accidentally prefer.</p>
 *
 * <h3>Why a map of FUNCTIONS and not a map of strings</h3>
 * <p>Four of the nine kinds carry a fact that belongs INSIDE the sentence — the stage that was
 * entered, the party that was added, the status a party moved to. A `Record<string, string>` plus
 * interpolation at the call site would put half the copy here and half in the page, and the half
 * in the page is the half nobody finds when the wording changes. A copy function keeps the whole
 * sentence in one place and makes the fact it needs an argument rather than a lookup.</p>
 *
 * <h3>Closed map, and what an unknown kind does</h3>
 * <p>{@link MILESTONE_KIND_COPY} is closed over today's nine kinds, and
 * `milestoneCopy.test.ts` pins it to `StatusMilestoneKinds.All` in BOTH directions by reading
 * that C# file — a kind the platform grows with no English here fails the build, and so does an
 * entry for a kind the platform does not have. The run-time fallback is the safety net for the
 * window between an engine deploy and a portal one: an unknown kind renders its TOKEN VERBATIM
 * and is marked {@link MilestoneLine.unmapped}, never dropped. A milestone that silently vanished
 * would make a file's history read as emptier than it is — which is the exact dishonesty
 * `other_event_count` exists to prevent one layer up.</p>
 *
 * <h3>No actor, ever (Pattern S6)</h3>
 * <p>There is nothing here that could name one: the wire record carries no `actor`, because an
 * actor string is `human-console:&lt;email&gt;` under ADR-073 and carrying one would put an email
 * on every line of every timeline. The one identity that does cross is a party's DISPLAY NAME on
 * a party milestone, which is the same best-effort value the party ledger already shows.</p>
 */
import type { JobStatusMilestone } from "../api/types.js";
import { partyStatusLabel } from "./partyChips.js";

/**
 * The facts a milestone can carry into its own sentence — the four optional members of
 * `JobStatusMilestone`, and nothing else.
 *
 * <p>Deliberately NOT `JobStatusMilestone` itself: a copy function that could read `kind` would be
 * free to branch on it, which is the one thing the map is for. Each entry gets the facts and not
 * the identity. (A whole milestone is still accepted at the call site — TypeScript's excess
 * property check applies to object literals, so passing the wider record is exactly as safe and
 * saves every caller a destructure.)</p>
 */
export interface MilestoneParts {
  /** On a `stage_entered` milestone: the stage that was entered. */
  stage_label?: string;
  /** Present exactly when {@link stage_label} is — `state_id` means nobody has named it. */
  stage_label_source?: "declared" | "state_id";
  /** On a party milestone: that party's display name. Never an email, never a row key. */
  subject?: string;
  /** On a `party_outcome` milestone: the party's NEW `job_parties.status`, RAW. */
  outcome?: string;
}

/** One kind's whole sentence, composed from the facts that milestone carries. */
export type MilestoneCopy = (parts: MilestoneParts) => string;

/**
 * What a party milestone calls its subject when the ledger has no name for it.
 *
 * <p>`party_ref->>'name'` is best-effort and genuinely nullable, and a milestone whose subject
 * cannot be named is still a real thing that happened. So the sentence keeps its shape and loses
 * the name, rather than the row being dropped or a row key being printed in its place.</p>
 */
const UNNAMED_PARTY = "A party";

/** The party on a party milestone, named when the ledger could name them. */
function who(parts: MilestoneParts): string {
  const subject = parts.subject?.trim();
  return subject ? subject : UNNAMED_PARTY;
}

/**
 * The `stage_entered` sentence — and the one place this module has to make the SAME honesty call
 * the board's stage cell makes.
 *
 * <p>A `state_id` source means the declaration names no stage for that state, so `stage_label` is
 * the RAW ENGINE STATE. Interpolating it would read as "Moved to AWAITING_KYC", which dresses
 * machine vocabulary as a sentence someone wrote — exactly what AC-6 forbids. So the sentence says
 * what is true, and the raw state travels beside it on {@link MilestoneLine.rawStage} for the page
 * to render plainly, as the board does.</p>
 */
function stageSentence(parts: MilestoneParts): string {
  if (parts.stage_label === undefined) return "Moved to the next stage";
  if (parts.stage_label_source === "state_id") return "Moved to a stage that is not named yet";
  return `Moved to ${parts.stage_label}`;
}

/**
 * Every kind the platform ships, in English.
 *
 * <p>Key order is `StatusMilestoneKinds.All`'s, so the two lists can be read side by side. Order
 * carries no other meaning: the timeline renders in the engine's own chronological order, never in
 * this map's.</p>
 */
export const MILESTONE_KIND_COPY: Readonly<Record<string, MilestoneCopy>> = {
  file_opened: () => "File opened",
  stage_entered: stageSentence,
  // No subject on the wire for this kind, and none is invented: WHICH form came back is a
  // question the outstanding-items ledger answers, and it answers it by the form's declared label
  // rather than by a form id.
  form_received: () => "A form came back",
  party_added: (parts) => `${who(parts)} was added to the file`,
  // The outcome is a raw `job_parties.status`, so its English comes from the ONE place that owns
  // that vocabulary — never a second copy of "awaiting_form means link sent" written here.
  party_outcome: (parts) =>
    parts.outcome === undefined
      ? `${who(parts)}: status changed`
      : `${who(parts)}: ${partyStatusLabel(parts.outcome)}`,
  // "An approval", not "approved by X": the record carries no actor and this sentence must not
  // imply one. Which gate it was is the timeline's position, not its copy.
  approved: () => "An approval was given",
  rejected: () => "An approval was refused",
  completed: () => "File completed",
  failed: () => "File failed",
};

/** One rendered timeline line: the sentence, and the two things the page needs to render it. */
export interface MilestoneLine {
  /** The engine's kind. A key and a test handle — the page renders {@link text}, never this. */
  kind: string;
  /** What a staffer reads. The RAW KIND TOKEN when this portal has no English for it yet. */
  text: string;
  /** True when {@link text} is the raw token rather than English — see the module doc. */
  unmapped: boolean;
  /**
   * The RAW ENGINE STATE, on a stage milestone the declaration has not named. `null` otherwise,
   * including on every declared stage — the page shows this plainly, as machine vocabulary, and
   * marks it as not-yet-named exactly as the board's stage cell does.
   */
  rawStage: string | null;
}

/**
 * One milestone, as a line a staffer can read.
 *
 * <p>Total by construction: every kind either has copy or falls back to its own token, so this
 * never returns nothing and a timeline can never be shorter than the milestones it was given.</p>
 */
export function milestoneLine(milestone: JobStatusMilestone): MilestoneLine {
  const copy = MILESTONE_KIND_COPY[milestone.kind];
  if (copy === undefined) {
    return { kind: milestone.kind, text: milestone.kind, unmapped: true, rawStage: null };
  }

  const rawStage =
    milestone.stage_label_source === "state_id" && milestone.stage_label !== undefined
      ? milestone.stage_label
      : null;

  return { kind: milestone.kind, text: copy(milestone), unmapped: false, rawStage };
}
